import { FontAwesome5 } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import { useMutation } from "@tanstack/react-query";
import { CheckBox, Text } from "@ui-kitten/components";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import Button from "components/Button";
import ConfirmPurchaseButton from "components/ConfirmPurchaseButton";
import EventBanner from "components/EventBanner";
import FanRallyTOS from "components/FanRallyTOSDialog/FanRallyTOS";
import FanRallyTOSDialog from "components/FanRallyTOSDialog/FanRallyTOSDialog";
import HiddenView from "components/HiddenView";
import MainContainer from "components/MainContainer";
import Spacer from "components/Spacer";
import { resetPremiumInventory } from "features/slices/reservationFlowSlice";
import {
  AccountTicketDetails,
  EventNoticeDetails,
  PlanPackageComponentDetails,
  PremiumCodeDetails,
  SubscriptionDetails,
  TicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useCurrentUser from "hooks/useCurrentUser";
import useIsSSO from "hooks/useIsSSO";
import useMarketingAssetsSections from "hooks/useMarketingAssetsSections";
import { uniqBy } from "lodash";
import { ReserveScreenProps } from "navigation/types";
import i18n from "plugins/localization";
import { updateAccountFetcher } from "queries/account.queries";
import { requestBookingRequest } from "queries/bookingRequest.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import {
  calculateChargeRequest,
  calculateReservationUpdateChargesRequest,
  reserveForReservationUpdateRequest,
  reserveTicketsFetcher,
} from "queries/ticket.queries";
import React, { useEffect, useMemo, useState } from "react";
import { Platform, Pressable, View } from "react-native";
import PaymentMethodCard from "screens/Account/components/PaymentMethodCard";
import VerifyPhonePopup from "screens/Dashboard/components/VerifyPhonePopup";
import { formatEventDate, isSportsEvent } from "utils/eventUtils";
import { toEventDetail, toMyEvents, toScehdule } from "utils/navigationUtils";
import { getPlanType } from "utils/planPackageUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import { getMaxSeatsAllowedPerReservation } from "utils/tenantUtils";
import toast from "utils/toast";
import CartSummary from "./components/CartSummary";
import HoldDetails, { FanRallyMarketPlace } from "./components/HoldDetails";
import ImportantMessageDialog from "./components/ImportantMessageDialog";
import PremiumHoldDetails from "./components/PremiumHoldDetails";
import RefundDetails from "./components/RefundDetails";
import {
  findComponentById,
  findUserById,
  getRemainingAvailableSlotsForComponent,
  getRemainingPerEventMaxForComponent,
  getRemainingQuantityForComponent,
  isPrimaryComponent,
  sortSubscriptionsByPrimaryMember,
  usersNotGoing,
} from "./helpers";

// checkout page for:
//  Standard flow: RESERVATION, VOUCHER and BANK
//  Preimum flow: SUITE
// If the premiumCode and premiumClass(set in PremiumCodeDetails screen) aren't null, then use the premium flow

export const getNewlyAddedEvent = (
  newTickets: TicketDetails[],
  accountEvents: AccountTicketDetails[],
): AccountTicketDetails => {
  const newTicket = newTickets[0];
  return accountEvents.find((accEvent) =>
    accEvent.tickets.some((ticket) => ticket.id === newTicket.id),
  )!;
};

const Reserve: React.FC<ReserveScreenProps> = () => {
  const navigation = useNavigation();
  // redux
  const {
    selectedEvent,
    selectedSubscription,
    originalHeldDetail,
    actualHeldDetail,
    premiumInventory,
    selectedPremiumCode,
    isChangingSeats,
    originalEventId,
    originalTickets,
    keptOriginalTicketIds,
  } = useAppSelector((state) => state.reservationFlow);

  const dispatch = useAppDispatch();

  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const barcodeProvider = selectedEvent?.barcodeProvider || "FANRALLY";

  const isPremiumFlow = premiumInventory !== null;

  const isTicketApprovalEnabled =
    selectedSubscription?.planPackage.ticketApprovalEnabled;

  const userSelfRequestCompTicketsEnabled =
    selectedSubscription?.planPackage.userSelfRequestCompTicketsEnabled;

  // custom hooks
  const {
    account,
    accountEvents: events,
    fetchAccount,
    isFetching: fetchingAccount,
  } = useAccount();
  const { authMethod } = useIsSSO();
  const { currentUser, currentMember } = useCurrentUser();

  const { isPremiumInventoryMarketingAssets } = useMarketingAssetsSections(
    selectedSubscription?.planPackage.id,
  );
  // catch cases when url is directly accessed
  if (!selectedEvent || !selectedSubscription || !account) {
    return null;
  }

  // local states
  const [matchedSubscriptions, setMatchedSubscriptions] = useState<
    SubscriptionDetails[]
  >([]);
  const [selectedUsersAndComponents, setSelectedUsersAndComponents] = useState<
    number[]
  >([]);
  const [notices, setNotices] = useState<EventNoticeDetails[]>([]);
  const [importantMessageDialog, setImportantMessageDialog] = useState(false);
  const [TOSDialog, setTOSDialog] = useState(false);
  const [TOSChecked, setTOSChecked] = useState(false);
  const [verifyPhonePopupVisible, setVerifyPhonePopupVisible] = useState(false);

  // react queries
  const { mutateAsync: updateAccount, isLoading: updatingAccount } =
    useMutation(updateAccountFetcher);
  const { mutateAsync: reserve, isLoading: reserving } = useMutation(
    reserveTicketsFetcher,
  );
  const { mutateAsync: reserveForReservationUpdate, isLoading: updating } =
    useMutation(reserveForReservationUpdateRequest);
  const { mutateAsync: requestBooking, isLoading: requesting } = useMutation(
    requestBookingRequest,
  );
  // helper function for useCalculateChargeQuery`

  const componentSupportsSelectedEvent = (
    component: PlanPackageComponentDetails,
  ) => {
    if (component.providerTemplateIds.length === 0) {
      return true;
    }
    return component.providerTemplateIds.includes(
      selectedEvent.providerTemplateId,
    );
  };

  const primaryComponentForEvent =
    selectedSubscription.planPackage.components.find(
      (component) => component.primary,
    )!;

  const findPrimaryComponentId = () => {
    return primaryComponentForEvent.id;
  };

  const getCalculateChargeItems = () => {
    return actualHeldDetail.map((ticket, index) => {
      const user = findUserById(
        selectedUsersAndComponents[index],
        matchedSubscriptions,
      );
      const planPackageComponentId =
        (!isPremiumFlow && user) || isPremiumFlow
          ? findPrimaryComponentId()
          : selectedUsersAndComponents[index];

      return {
        planPackageId: ticket.planPackageId,
        // in the selectedUsersAndComponents array,
        // if the id in the given index is a user id,
        // then we should use the primary plan package component's id
        // otherwise if it is a component id, then we should use it instead
        planPackageComponentId,
        ticketId: ticket.id,
      };
    });
  };

  const handleCalculateCharge = () => {
    calculateCharge({
      accountId: account.id,
      eventId: selectedEvent.eventId,
      items: getCalculateChargeItems(),
    });
  };

  const releaseTicketIds = originalTickets
    .filter((t) => !keptOriginalTicketIds.includes(t.id))
    .map((t) => t.id);

  const handleCalculateReservationUpdateChange = () => {
    calculateReservationUpdateCharge({
      accountId: account.id,
      eventId: selectedEvent.eventId,
      newTicketItems: getCalculateChargeItems(),
      ticketRefundIds: releaseTicketIds,
    });
  };

  const { data: standardReservationCharge, mutateAsync: calculateCharge } =
    useMutation(calculateChargeRequest);

  const {
    data: reservationUpdateCharge,
    mutateAsync: calculateReservationUpdateCharge,
  } = useMutation(calculateReservationUpdateChargesRequest);

  // Normalize charge details
  const charge = isChangingSeats
    ? reservationUpdateCharge?.chargeDetails
    : standardReservationCharge;

  // helper functions and variables
  const heldTicket = actualHeldDetail[0];
  const planType = getPlanType(selectedSubscription.planPackage);

  // renders the price details for premium seating, type === 'GA' only
  const renderPremiumLocationSubtitle = (
    premiumCode: PremiumCodeDetails | null,
  ) => {
    if (
      !premiumCode ||
      !charge ||
      premiumInventory?.premiumClass.type === "SEATS"
    )
      return null;

    return (
      <View>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Text category="s1">Quantity: {actualHeldDetail.length}</Text>
        </View>
      </View>
    );
  };

  const handleFRTOSClick = () => {
    if (authMethod === "ballpark" || Platform.OS !== "web") {
      setTOSDialog(true);
    } else {
      const wd = window.open()!;
      wd.document.write(FanRallyTOS);
      wd.document.title = "FanRally Marketplace Terms of Service";
    }
  };

  const hasOriginalHeldSections = useMemo(() => {
    if (heldTicket.seat.ga) {
      // for GA, only check if the section is the same
      return originalHeldDetail.section === heldTicket.seat.section;
    }
    return (
      originalHeldDetail.section === heldTicket.seat.section &&
      originalHeldDetail.row === heldTicket.seat.row
    );
  }, [heldTicket]);

  // Subscriptions in account associated with the selected plan package
  const planPackageSubscriptions = account.subscriptions.filter(
    (sub) =>
      filterIneligibleSubscriptions(sub) &&
      sub.planPackage.id === selectedSubscription.planPackage.id,
  );

  const findAndSetMatchedSubscriptions = async () => {
    if (planType === "RESERVATION") {
      const usersNotGoingIdSet = usersNotGoing({
        subscriptions: planPackageSubscriptions,
        eventId: selectedEvent.eventId,
        events,
        isChangingSeats,
        groupCode: isChangingSeats ? originalTickets[0]?.groupCode : undefined,
      });
      // find all users that are eligible to this event
      const eligibleUsers: SubscriptionDetails[] = [];
      planPackageSubscriptions.forEach((sub) => {
        if (!usersNotGoingIdSet.has(sub.member.user.id)) {
          eligibleUsers.push(sub);
        }
      });
      setMatchedSubscriptions(eligibleUsers);
    } else {
      setMatchedSubscriptions(planPackageSubscriptions);
    }
  };

  const initImportantMessages = () => {
    const notices = heldTicket.event.notices.filter((notice) =>
      notice.displayTypes.includes("CONFIRMATION"),
    );
    if (notices.length === 0) return;
    setNotices(notices);
    setImportantMessageDialog(true);
  };

  // Triggered when the user selector changes or initializing
  const addSelectedUserOrComponent = (
    id: number /*could be user id or component id*/,
    initialIndex: number,
  ) => {
    if (!id) return;

    const user = findUserById(id, matchedSubscriptions);
    const component = findComponentById(id, matchedSubscriptions);

    let newSelectedUsersOrComponents: any = [];
    const newId = user ? user.id : component!.id;
    setSelectedUsersAndComponents((oldSelectedUsersOrComponents) => {
      newSelectedUsersOrComponents = [...oldSelectedUsersOrComponents];
      newSelectedUsersOrComponents[initialIndex] = newId;
      return newSelectedUsersOrComponents;
    });
  };

  const getReserveOrRedeemItems = () => {
    return selectedUsersAndComponents.map((userOrComponent) => {
      const user = findUserById(userOrComponent, matchedSubscriptions);

      // If userOrComponentId is a user id, we are reserving using a RESERVATION PLAN
      if (user) {
        return {
          userId: userOrComponent,
          planPackageComponentId: findPrimaryComponentId(),
        };
      }

      /******* Reserving using a VOUCHER or BANK PLAN *******/
      // If userOrComponentId is a primary component id
      if (isPrimaryComponent(userOrComponent, selectedSubscription)) {
        return {
          planPackageComponentId: userOrComponent,
          userId: selectedSubscription.member.user.id,
        };
      }

      // guest component
      return {
        planPackageComponentId: userOrComponent,
      };
    });
  };

  const handleRequestTickets = async (config?: { complimentary: boolean }) => {
    if (!currentMember?.id) return;
    const { complimentary = false } = config || {};
    const items = getReserveOrRedeemItems();
    await requestBooking({
      planPackageId: selectedSubscription.planPackage.id,
      eventId: selectedEvent.eventId,
      items,
      complimentary,
    });
    toMyEvents();
    toast.success(
      "Your booking request was submitted successfully! You can track your request status here.",
    );
  };

  const handleSubmitButtonPress = () => {
    if (isTicketApprovalEnabled) {
      handleRequestTickets();
      return;
    }

    isChangingSeats ? handleChangeSeats() : handleReserveOrRedeem();
  };

  const shouldShowPriceDetails = useMemo(() => {
    if (["NIL", "RSVP"].includes(selectedEvent.eventType)) return false;
    return charge && !isChangingSeats;
  }, [charge, isChangingSeats, selectedEvent]);

  const handleChangeSeats = async () => {
    try {
      const reservedTickets = await reserveForReservationUpdate({
        accountId: account!.id,
        planPackageId: selectedSubscription!.planPackage.id,
        eventId: selectedEvent!.eventId,
        groupCode: originalTickets[0].groupCode!,
        items: getReserveOrRedeemItems(),
        releaseTicketIds,
      });
      await postReservationComplete(reservedTickets);
      toast.success("Congratulations! Your reservation has been updated!");
    } catch (error) {
      toMyEvents();
      fetchAccount();
      toast.error(
        "There was an issue reserving your new tickets. Your previous tickets have been released, and any associated payment will be refunded. You have not been charged for the new tickets. Please try reserving your seats again directly.",
      );
    }
  };

  const handleReserveOrRedeem = async () => {
    const seatPreference = account.seatPreference;
    const params = {
      eventId: selectedEvent.eventId,
      accountId: account.id,
      section: heldTicket.seat.section,
      row: heldTicket.seat.row,
      planPackageId: selectedSubscription.planPackage.id,
    };
    const tickets = await reserve({
      ...params,
      items: getReserveOrRedeemItems(),
    });
    const marketplaceOptIn =
      account.marketplace === false && Number(charge?.totalPrice) > 0;
    if (marketplaceOptIn) {
      await updateAccount({
        ...account,
        marketplace: marketplaceOptIn ? true : account.marketplace,
        seatPreference,
      });
    }
    await postReservationComplete(tickets);
    toast.success(i18n.t("common.reservationConfirmedMessage"));
  };

  const postReservationComplete = async (tickets: TicketDetails[]) => {
    const accountBFF = await fetchAccount(); // fetch updated account info
    const newlyAddedEvent = getNewlyAddedEvent(
      tickets,
      accountBFF.data!.events!,
    );
    toEventDetail(newlyAddedEvent.groupCode.groupCode);
  };

  const marketPlaceNotOptIn = () => {
    if (charge === undefined) return false;
    if (
      parseFloat(charge?.totalPrice) > 0 &&
      TOSChecked === false &&
      account.marketplace === false
    ) {
      return true;
    }
    return false;
  };

  // array of available guest components
  const guestComponents =
    matchedSubscriptions[0]?.planPackage.components.filter(
      (component) =>
        component.type.includes("GUEST") &&
        componentSupportsSelectedEvent(component),
    ) || [];

  const findGuestComponentsByType = (
    matchedSubscriptions: SubscriptionDetails[],
    type: "PAID" | "FREE",
  ) => {
    return matchedSubscriptions.reduce(
      (guestComponents: PlanPackageComponentDetails[], sub) => {
        let result: PlanPackageComponentDetails[] = [];
        for (const component of sub.planPackage.components) {
          if (
            component.type.includes("GUEST") &&
            componentSupportsSelectedEvent(component)
          ) {
            if (
              (type === "FREE" && component.name.includes("Included")) ||
              (type === "PAID" && component.name.includes("Paid"))
            ) {
              result.push(component);
            }
          }
        }
        return [...guestComponents, ...result];
      },
      [],
    );
  };

  // will produce an array of user ids and guest component ids which indicates the order of selectors,
  // for example: [member1,member2,freeGuest1,freeGuest2,paidGuest1,paidGuest2]
  const prefillSelectorArray = useMemo(() => {
    if (matchedSubscriptions.length === 0) return [];
    const result: number[] = [];
    const maxSeatsAllowed = getMaxSeatsAllowedPerReservation(tenantConfig);
    const getRemainingQuantityForComponentParams = {
      subscriptions: planPackageSubscriptions,
      events: events || [],
      isChangingSeats,
      originalEventId,
      eventId: selectedEvent.eventId,
      maxSeatsAllowed,
      groupCode: isChangingSeats
        ? (originalTickets[0]?.groupCode ?? undefined)
        : undefined,
      keptOriginalTicketIds: isChangingSeats ? keptOriginalTicketIds : [],
    };
    let freeGuestComponents = findGuestComponentsByType(
      matchedSubscriptions,
      "FREE",
    ).filter(
      (component) =>
        getRemainingQuantityForComponent({
          component,
          ...getRemainingQuantityForComponentParams,
        }) > 0,
    );
    let paidGuestComponents = findGuestComponentsByType(
      matchedSubscriptions,
      "PAID",
    ).filter(
      (component) =>
        getRemainingQuantityForComponent({
          component,
          ...getRemainingQuantityForComponentParams,
        }) > 0,
    );

    // All non-primary components with perGameRedemptionMinimum, scanned directly from plan components
    // rather than relying on name-based categorization (findGuestComponentsByType).
    const requiredMinimumComponents = uniqBy(
      matchedSubscriptions.flatMap((sub) =>
        sub.planPackage.components.filter(
          (comp) =>
            !comp.primary &&
            comp.perGameRedemptionMinimum > 0 &&
            componentSupportsSelectedEvent(comp),
        ),
      ),
      "id",
    );

    // Reserve slots for non-primary components that have a perGameRedemptionMinimum,
    // so the primary loop doesn't consume all available seats before they get a chance.
    const totalRequiredGuestMinimumSlots = Math.min(
      requiredMinimumComponents.reduce(
        (total, comp) => total + comp.perGameRedemptionMinimum,
        0,
      ),
      actualHeldDetail.length,
    );

    if (planType === "RESERVATION") {
      // Sort matched subscriptions so that primary member's subscriptions appear first
      // This ensures the primary member appears first in the selectors
      sortSubscriptionsByPrimaryMember(matchedSubscriptions);
      matchedSubscriptions.forEach((sub) => {
        if (
          result.length >=
          actualHeldDetail.length - totalRequiredGuestMinimumSlots
        )
          return;

        // When changing seats and keeping some originals, skip members whose primary
        // reservation slot is already occupied by a kept ticket. Since perEventMax is
        // typically 1 per member, adding them again here would wrongly displace a
        // guest slot — the new seats should default to guest components instead.
        if (isChangingSeats) {
          const hasKeptPrimaryTicket = originalTickets.some(
            (t) =>
              keptOriginalTicketIds.includes(t.id) &&
              t.planPackageComponentId === primaryComponentForEvent.id &&
              t.user?.id === sub.member.user.id,
          );
          if (hasKeptPrimaryTicket) return;
        }

        result.push(sub.member.user.id);
      });
    } else if (planType === "VOUCHER") {
      const maxVouchers = getRemainingAvailableSlotsForComponent({
        component: primaryComponentForEvent,
        subscriptions: planPackageSubscriptions,
        events,
        eventId: selectedEvent.eventId,
        isChangingSeats,
        originalEventId,
        maxSeatsAllowed,
        groupCode: isChangingSeats
          ? (originalTickets[0]?.groupCode ?? undefined)
          : undefined,
        keptOriginalTicketIds: isChangingSeats ? keptOriginalTicketIds : [],
      });

      let counter = 0;
      const primaryVoucherComponentId = primaryComponentForEvent.id;

      while (
        counter < maxVouchers &&
        counter < actualHeldDetail.length - totalRequiredGuestMinimumSlots
      ) {
        result.push(primaryVoucherComponentId);
        counter++;
      }
    } else {
      // plan type is BANK
      let counter = 0;

      const availableBankSlotsForEvent = getRemainingPerEventMaxForComponent({
        component: primaryComponentForEvent,
        subscriptions: planPackageSubscriptions,
        events,
        eventId: selectedEvent.eventId,
        isChangingSeats,
        maxSeatsAllowed,
        groupCode: isChangingSeats
          ? (originalTickets[0]?.groupCode ?? undefined)
          : undefined,
        keptOriginalTicketIds: isChangingSeats ? keptOriginalTicketIds : [],
      });

      while (
        counter < availableBankSlotsForEvent &&
        counter < actualHeldDetail.length - totalRequiredGuestMinimumSlots
      ) {
        result.push(primaryComponentForEvent.id);
        counter++;
      }
    }

    // Merge all guest components, ensuring required-minimum components are included
    // even if their names don't match "Paid"/"Included" patterns used by findGuestComponentsByType.
    const mergedUniqGuestComponents = uniqBy(
      [
        ...freeGuestComponents,
        ...paidGuestComponents,
        ...requiredMinimumComponents,
      ],
      "id",
    );

    // Make sure free guest components appear first in the selector array
    mergedUniqGuestComponents.sort((component) => {
      if (component.type.includes("Included")) {
        return -1;
      }
      return 1;
    });

    const selectedPlanSubscriptions = account.subscriptions.filter(
      (sub) => sub.planPackage.id === selectedSubscription.planPackage.id,
    );

    const requiredComponentIdsSet = new Set(
      requiredMinimumComponents.map((c) => c.id),
    );

    // Phase 1: Fill minimum required slots for each required-minimum component first,
    // before any unlimited component can consume all remaining slots.
    for (const comp of requiredMinimumComponents) {
      let counter = 0;
      while (
        counter < comp.perGameRedemptionMinimum &&
        result.length < actualHeldDetail.length &&
        result.length < maxSeatsAllowed
      ) {
        result.push(comp.id);
        counter++;
      }
    }

    // Phase 2: Fill remaining slots using availability-based logic,
    // subtracting slots already added in Phase 1.
    for (const guestComponent of mergedUniqGuestComponents) {
      if (
        result.length >= actualHeldDetail.length ||
        result.length >= maxSeatsAllowed
      )
        break;

      const perEventMaxForComponent = getRemainingPerEventMaxForComponent({
        component: guestComponent,
        subscriptions: selectedPlanSubscriptions,
        events,
        eventId: selectedEvent.eventId,
        isChangingSeats,
        maxSeatsAllowed,
        groupCode: isChangingSeats
          ? (originalTickets[0]?.groupCode ?? undefined)
          : undefined,
        keptOriginalTicketIds: isChangingSeats ? keptOriginalTicketIds : [],
      });
      const remainingQuantityForComponent = getRemainingQuantityForComponent({
        component: guestComponent,
        subscriptions: selectedPlanSubscriptions,
        events,
        eventId: selectedEvent.eventId,
        isChangingSeats,
        originalEventId,
        maxSeatsAllowed,
        groupCode: isChangingSeats
          ? (originalTickets[0]?.groupCode ?? undefined)
          : undefined,
        keptOriginalTicketIds: isChangingSeats ? keptOriginalTicketIds : [],
      });
      const remainingAvailableSlotsForComponent = Math.min(
        perEventMaxForComponent,
        remainingQuantityForComponent,
      );

      const alreadyFilled = requiredComponentIdsSet.has(guestComponent.id)
        ? result.filter((id) => id === guestComponent.id).length
        : 0;

      if (remainingAvailableSlotsForComponent === maxSeatsAllowed) {
        // if component has unlimited guest pass amount, fill the result array with its component id
        while (result.length < actualHeldDetail.length) {
          result.push(guestComponent.id);
        }
        break;
      } else {
        // if component has a fixed amount of guest pass, fill remaining capacity beyond what Phase 1 already added
        const slotsToFill = Math.max(
          0,
          remainingAvailableSlotsForComponent - alreadyFilled,
        );
        let counter = 0;
        while (
          counter < slotsToFill &&
          result.length < actualHeldDetail.length &&
          result.length < maxSeatsAllowed
        ) {
          result.push(guestComponent.id);
          counter++;
        }
      }
    }
    return result;
  }, [
    matchedSubscriptions,
    primaryComponentForEvent,
    originalTickets,
    keptOriginalTicketIds,
  ]);

  const confirmPurchaseButtonText = useMemo(() => {
    if (!charge) return "";
    if (isChangingSeats) return "Update Reservation";
    if (isTicketApprovalEnabled) return "Request Tickets";
    const { totalCreditCardCharges, totalPoints } = charge!;
    const totalCharge = Number(totalCreditCardCharges);
    if (totalCharge === 0) {
      if (planType === "BANK") {
        // if payment is covered by bank balance, show Purchase with Bank,
        // if no payment required, show Reserve
        return Number(totalPoints) > 0
          ? i18n.t("common.purchaseAndBook")
          : i18n.t("common.book");
      } else {
        // plan type is RESERVATION or Voucher
        return planType === "RESERVATION"
          ? i18n.t("common.book")
          : i18n.t("common.redeem");
      }
    } else {
      // total charge is greater than 0
      return planType === "VOUCHER"
        ? i18n.t("common.purchaseAndRedeem")
        : i18n.t("common.purchaseAndBook");
    }
  }, [charge, planType]);

  // For marketing assets inventory, we don't want to show "X seats on hold" text
  const bannerText = useMemo(() => {
    if (
      premiumInventory &&
      isPremiumInventoryMarketingAssets(premiumInventory)
    ) {
      return (
        premiumInventory.premiumClass.name || premiumInventory.premiumClass.code
      );
    }
    return undefined;
  }, [premiumInventory]);

  // standard checkout flow
  useEffect(() => {
    if (selectedUsersAndComponents.length !== actualHeldDetail.length) return;
    if (isChangingSeats) {
      handleCalculateReservationUpdateChange();
    } else {
      handleCalculateCharge();
    }
  }, [selectedUsersAndComponents]);

  useEffect(() => {
    if (premiumInventory !== null) {
      handleCalculateCharge();
    }
  }, [premiumInventory]);

  useEffect(() => {
    initImportantMessages();
    findAndSetMatchedSubscriptions();

    // When leaving the page, set premium inventory to null
    return () => {
      dispatch(resetPremiumInventory());
    };
  }, []);

  const isRSVP = selectedEvent.eventType === "RSVP";

  const isLoading =
    reserving || updating || requesting || updatingAccount || fetchingAccount;
  return (
    <MainContainer
      grayBackground
      goBackAction={() => {
        if (isPremiumFlow) {
          dispatch(resetPremiumInventory());
          toScehdule();
        } else {
          navigation.goBack();
        }
      }}
    >
      <VerifyPhonePopup
        visible={verifyPhonePopupVisible}
        onCancel={() => setVerifyPhonePopupVisible(false)}
      />
      <ImportantMessageDialog
        notices={notices}
        visible={importantMessageDialog}
        setVisible={setImportantMessageDialog}
      />
      <FanRallyTOSDialog visible={TOSDialog} setVisible={setTOSDialog} />
      <View style={{ padding: 15 }}>
        <EventBanner
          event={actualHeldDetail[0].event}
          type="RESERVE"
          heldTicketLength={actualHeldDetail.length}
          bannerText={bannerText}
        />
        <View className="mt-2">
          {tenantConfig?.requirePhoneVerification !== false &&
            !currentUser?.phoneVerified && (
              <View className="flex-row">
                <Text category="p1" status="danger">
                  Phone number not verified, {""}
                </Text>
                <Pressable
                  className="flex-row"
                  onPress={() => setVerifyPhonePopupVisible(true)}
                >
                  <Text status="info">Click here </Text>
                </Pressable>
                <Text status="danger">to verify your phone number</Text>
              </View>
            )}
          {!hasOriginalHeldSections && (
            <Text category="p1" status="danger">
              {i18n.t("screens.Reserve.seatsAlreadyTaken")}
            </Text>
          )}
        </View>
        <Spacer>
          {!isSportsEvent(selectedEvent.eventType) && (
            <BaseCard title="Event Details">
              <BaseCardListItem
                title={selectedEvent.eventName}
                showArrow={false}
              ></BaseCardListItem>
              {selectedEvent.eventType !== "EXPERIENCE" && (
                <BaseCardListItem
                  title={formatEventDate(selectedEvent)}
                  showArrow={false}
                ></BaseCardListItem>
              )}
              <BaseCardListItem
                title={`${selectedEvent.venueName} - ${selectedEvent.venueCity}, ${selectedEvent.venueState}`}
                showArrow={false}
              ></BaseCardListItem>
            </BaseCard>
          )}
        </Spacer>
        {/* premium checkout flow */}
        {isPremiumFlow && (
          <BaseCard title="Location" style={{ marginBottom: 8 }}>
            <BaseCardListItem
              title={premiumInventory.premiumClass.name}
              showArrow={false}
            >
              {renderPremiumLocationSubtitle(selectedPremiumCode)}
            </BaseCardListItem>
          </BaseCard>
        )}
        {isPremiumFlow && premiumInventory.premiumClass.type === "SEATS" && (
          <Spacer>
            {actualHeldDetail.map((detail, index) => (
              <PremiumHoldDetails
                key={detail.id}
                ticket={detail}
                charge={charge}
                index={index}
              ></PremiumHoldDetails>
            ))}
          </Spacer>
        )}
        {isChangingSeats && (
          <View>
            <Text category="h6">
              {`Existing Reservation: ${
                originalTickets[0].event.name
              } ${formatEventDate(originalTickets[0].event)}`}
            </Text>
            <Spacer />
            <BaseCard>
              <Text>
                {originalTickets[0].seat.ga
                  ? i18n.t("common.generalAdmission")
                  : `${i18n.t("common.section")}: ${
                      originalTickets[0].seat.sectionDisplay
                    } ` +
                    `${i18n.t("common.row")}: ${
                      originalTickets[0].seat.rowDisplay
                    } `}
              </Text>
              <BaseCardListItem
                showArrow={false}
                title={() => {
                  return (
                    <Text>
                      {`${i18n.t("common.seat", {
                        count: 1,
                      })}: ${originalTickets
                        .map((ticket) => ticket.seat.seatDisplay)
                        .join(", ")}`}
                    </Text>
                  );
                }}
              />
            </BaseCard>
            {/* {reservationUpdateCharge && (
              <Text style={{ marginTop: 8 }}>
                Total Cost:{" "}
                {formatCurrency(
                  reservationUpdateCharge.refundDetails.totalPrice,
                  {
                    priceType: reservationUpdateCharge.refundDetails.priceType,
                  }
                )}
              </Text>
            )} */}
            {reservationUpdateCharge && keptOriginalTicketIds.length === 0 && (
              <>
                <Spacer />
                <RefundDetails
                  refundDetails={reservationUpdateCharge.refundDetails}
                />
              </>
            )}
          </View>
        )}
        {/* standard checkout flow */}
        {/* We want to still render HoldDetails even if we hide it in premium flow because it will update the
        selectedUsersAndComponents state so that getReserveOrRedeemItems will return the correct items. */}
        <HiddenView shouldHide={isPremiumFlow}>
          {matchedSubscriptions.length > 0 && (
            <Spacer>
              {isChangingSeats && (
                <View>
                  <Text category="h6">
                    {keptOriginalTicketIds.length > 0
                      ? "New Tickets Added"
                      : "New Reservation: "}
                  </Text>
                  <Spacer />
                </View>
              )}
              <BaseCard title="Ticket Details">
                {actualHeldDetail.map((detail, index) => (
                  <HoldDetails
                    ticket={detail}
                    key={detail.id}
                    planType={planType}
                    charge={charge}
                    matchedSubscriptions={matchedSubscriptions}
                    initialIndex={index}
                    selectedUsersAndComponents={selectedUsersAndComponents}
                    addSelectedUserOrComponent={addSelectedUserOrComponent}
                    guestComponents={guestComponents}
                    prefillSelectorArray={prefillSelectorArray}
                    selectedSubscription={selectedSubscription}
                  />
                ))}
              </BaseCard>
            </Spacer>
          )}
        </HiddenView>
        {charge &&
          reservationUpdateCharge?.refundDetails &&
          isChangingSeats && (
            <Spacer>
              <CartSummary charge={charge} />
            </Spacer>
          )}
        {!charge && !isRSVP && (
          <Text category="h4" style={{ marginVertical: 8 }}>
            Loading charge details...
          </Text>
        )}
        {/* only show payment method card if account has a credit card on file */}
        {!isRSVP &&
          Boolean(account.billingLast4) &&
          Number(charge?.totalCreditCardCharges) > 0 && (
            <View style={{ marginBottom: 8 }}>
              <PaymentMethodCard
                title={i18n.t("screens.Reserve.paymentDetails")}
              />
            </View>
          )}
        {shouldShowPriceDetails && <CartSummary charge={charge!} />}
        {/** <!-- We need to update the UI, so that if the "marketplace" flag is false,
        and a user attempts to reserve a ticket with associated charges,
        then we prompt the user to opt into having their account data used in the FanRally marketplace.
        https://github.com/FanRally/fanrally-core/issues/506
         --> */}
        {!isRSVP &&
          charge &&
          parseFloat(charge.totalPrice) > 0 &&
          account.marketplace === false && (
            <View style={{ marginTop: 20 }}>
              <View
                style={{
                  marginTop: 15,
                  flexDirection: "row",
                  paddingHorizontal: 10,
                }}
              >
                <CheckBox
                  onChange={setTOSChecked}
                  checked={TOSChecked}
                  testID="TOS-checkbox"
                ></CheckBox>
                <Text style={{ textAlign: "left", marginLeft: 15 }}>
                  By clicking {confirmPurchaseButtonText}, you acknowledge that
                  you have read and agree to our{" "}
                  <Text
                    onPress={handleFRTOSClick}
                    style={{
                      color: "rgb(36, 152, 243)",
                      fontSize: 12,
                      fontWeight: "700",
                    }}
                  >
                    FanRally Marketplace Terms of Service
                  </Text>
                </Text>
              </View>
            </View>
          )}
        {!isRSVP &&
          !isChangingSeats &&
          Number(charge?.totalCreditCardCharges) > 0 && (
            <View style={{ marginVertical: 16 }}>
              <FanRallyMarketPlace
                name={i18n.t("screens.Reserve.poweredByFanRally")}
              />
            </View>
          )}
        {charge && !userSelfRequestCompTicketsEnabled ? (
          <View style={{ marginTop: 30 }}>
            <ConfirmPurchaseButton
              charge={Number(charge.totalCreditCardCharges)}
              buttonText={confirmPurchaseButtonText}
              loading={isLoading}
              disabled={isLoading || marketPlaceNotOptIn()}
              onPress={handleSubmitButtonPress}
            />
          </View>
        ) : null}
        {userSelfRequestCompTicketsEnabled ? (
          <View style={{ marginTop: 30 }}>
            <Button
              onPress={() => handleRequestTickets({ complimentary: true })}
              disabled={isLoading || marketPlaceNotOptIn()}
            >
              Request Comp Tickets
            </Button>
          </View>
        ) : null}
        {barcodeProvider === "TFL" && (
          <View className="mt-10 flex-row">
            <View className="flex-row items-center gap-2 w-full">
              <FontAwesome5
                name="calendar-alt"
                size={24}
                color="rgb(117, 117, 117)"
              />
              <Text>
                Your tickets will be delivered before your event. You'll receive
                an email from the venue's ticketing platform.
              </Text>
            </View>
          </View>
        )}
      </View>
    </MainContainer>
  );
};

export default Reserve;
